// @ts-nocheck -- consistent with the rest of src/app during the TS migration.
//
// Drag-and-drop file uploading.
//
// Deliberately does NOT introduce a second upload path. Dropped files are
// written into the dialog's existing <input type="file"> and its `change` event
// is fired, so the selection summary, the supported-extension filtering, the
// aircraft/monitor fields, and the submit handler all behave exactly as they do
// for files picked through "Choose Files". A parallel drop-upload pipeline would
// have to re-implement every one of those and would drift from them.

const DRAG_ACTIVE_CLASS = "is-dragover";

/**
 * Replace an <input type="file">'s selection with `files`.
 *
 * `input.files` is only assignable from a FileList, which cannot be constructed
 * directly — a DataTransfer is the sanctioned way to build one. Returns false
 * when the browser refuses (older engines), so callers can fall back rather
 * than silently dropping the user's files.
 */
export function assignFilesToInput(input, files) {
  const list = [...(files ?? [])];
  if (!input || !list.length) return false;
  try {
    const transfer = new DataTransfer();
    for (const file of list) transfer.items.add(file);
    input.files = transfer.files;
  } catch {
    return false;
  }
  input.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
}

// Depth cap for dropped folders. Monitor exports nest a couple of levels at
// most; anything deeper is either a mistake or a symlink loop, and walking it
// would hang the drop with no feedback.
const MAX_DIRECTORY_DEPTH = 8;

/** Promise wrapper for the callback-based FileSystemDirectoryReader. */
function readDirectoryEntries(reader) {
  return new Promise((resolve) => {
    reader.readEntries((entries) => resolve(entries ?? []), () => resolve([]));
  });
}

/** Promise wrapper for FileSystemFileEntry.file(). Resolves null on error rather than rejecting. */
function fileFromEntry(entry) {
  return new Promise((resolve) => {
    entry.file((file) => resolve(file ?? null), () => resolve(null));
  });
}

/**
 * Walk one dropped entry, collecting files. Directories recurse.
 *
 * readEntries returns at most 100 entries per call and signals completion with
 * an empty batch, so a large folder MUST be read in a loop — a single call
 * silently truncates to the first 100 files, which would look like a
 * successful upload that quietly dropped most of the logs.
 */
async function collectEntry(entry, files, depth = 0) {
  if (!entry || depth > MAX_DIRECTORY_DEPTH) return;
  if (entry.isFile) {
    const file = await fileFromEntry(entry);
    if (file) {
      // Preserve the path so the dialog's list and the server see the same
      // shape a "Choose Folder" pick produces.
      if (!file.webkitRelativePath && entry.fullPath) {
        try {
          Object.defineProperty(file, "webkitRelativePath", {
            value: String(entry.fullPath).replace(/^\//, ""),
            configurable: true,
          });
        } catch {
          /* read-only in some engines; the plain name still works */
        }
      }
      files.push(file);
    }
    return;
  }
  if (!entry.isDirectory) return;
  const reader = entry.createReader();
  for (;;) {
    const batch = await readDirectoryEntries(reader);
    if (!batch.length) break;
    for (const child of batch) await collectEntry(child, files, depth + 1);
  }
}

/**
 * Pull files out of a drop, descending into dropped folders.
 *
 * Async because the directory API is callback-based. Falls back to
 * `dataTransfer.files` when the entry API is unavailable — that path cannot see
 * folder contents, so `hadDirectory` reports whether a folder was dropped but
 * could not be read, letting the caller say so instead of silently uploading
 * nothing.
 */
export async function filesFromDropEvent(event) {
  const transfer = event?.dataTransfer;
  if (!transfer) return { files: [], hadDirectory: false };
  const items = transfer.items ? [...transfer.items] : [];
  const entries = [];
  let sawDirectory = false;
  for (const item of items) {
    if (item.kind !== "file") continue;
    // webkitGetAsEntry must be called synchronously, before any await: the
    // DataTransferItemList is neutered once the drop event handler yields.
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      if (entry.isDirectory) sawDirectory = true;
      entries.push(entry);
    } else {
      const file = item.getAsFile?.();
      if (file) entries.push({ isFile: true, __plainFile: file });
    }
  }
  if (!entries.length) {
    return { files: [...(transfer.files ?? [])], hadDirectory: false };
  }
  const files = [];
  for (const entry of entries) {
    if (entry.__plainFile) { files.push(entry.__plainFile); continue; }
    await collectEntry(entry, files);
  }
  // Only report an unreadable directory: a folder we walked successfully is not
  // something the caller should warn about.
  return { files, hadDirectory: sawDirectory && files.length === 0 };
}

/**
 * Wire one element as a drop target.
 *
 * Uses an enter/leave depth counter rather than a plain dragleave listener:
 * dragleave fires every time the pointer crosses into a CHILD element, so a
 * naive handler flickers the highlight off while the pointer is still inside
 * the zone. Returns a teardown function.
 */
export function createUploadDropzone(element, { onFiles, onDirectoryDropped } = {}) {
  if (!element?.addEventListener) return () => {};
  let depth = 0;

  const setActive = (active) => {
    element.classList?.toggle(DRAG_ACTIVE_CLASS, active);
  };
  // preventDefault on dragover is what tells the browser this is a valid drop
  // target; without it the browser navigates to the dropped file instead.
  const onDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  };
  const onDragEnter = (event) => {
    event.preventDefault();
    depth += 1;
    setActive(true);
  };
  const onDragLeave = () => {
    depth = Math.max(0, depth - 1);
    if (!depth) setActive(false);
  };
  const onDrop = (event) => {
    // preventDefault must happen synchronously, before any await: otherwise the
    // browser navigates to the dropped file. filesFromDropEvent is async (the
    // directory API is callback-based), so the result is handled in a .then
    // rather than destructured directly — destructuring a Promise here would
    // silently yield undefined and drop every file.
    event.preventDefault();
    depth = 0;
    setActive(false);
    void filesFromDropEvent(event).then(({ files, hadDirectory }) => {
      if (hadDirectory) onDirectoryDropped?.();
      if (files.length) onFiles?.(files);
    });
  };

  element.addEventListener("dragenter", onDragEnter);
  element.addEventListener("dragover", onDragOver);
  element.addEventListener("dragleave", onDragLeave);
  element.addEventListener("drop", onDrop);
  return () => {
    element.removeEventListener("dragenter", onDragEnter);
    element.removeEventListener("dragover", onDragOver);
    element.removeEventListener("dragleave", onDragLeave);
    element.removeEventListener("drop", onDrop);
  };
}

/**
 * Bind both dropzones: the one inside the upload dialog and the one at the
 * bottom of the aircraft Flights tab. Idempotent — safe to call on every
 * workspace render.
 */
export function setupUploadDropzones({
  openUploadDialog = () => {},
  selectedAircraftWorkspace = () => "",
  showAppToast = () => {},
  appReadOnlyMode = () => false,
} = {}) {
  const doc = globalThis.document;
  if (typeof doc?.querySelector !== "function") return;

  const fileInput = () => doc.querySelector("#uploadFileInput");
  const directoryNotice = () => showAppToast(
    "That folder could not be read — use Choose Folder in the upload dialog.",
    "info",
  );

  const bind = (element, handler) => {
    if (!element || element.dataset?.dropzoneBound === "1") return;
    if (element.dataset) element.dataset.dropzoneBound = "1";
    createUploadDropzone(element, { onFiles: handler, onDirectoryDropped: directoryNotice });
  };

  // Dialog: the input is already on screen, so write straight into it.
  bind(doc.querySelector("#uploadDialogDropzone"), (files) => {
    if (appReadOnlyMode()) return;
    if (!assignFilesToInput(fileInput(), files)) {
      showAppToast("This browser can't accept dropped files — use Choose Files.", "error");
    }
  });

  // Flights tab: open the dialog first, then fill it. openUploadDialog resets
  // the form and clears the input, so the files have to be assigned after it
  // returns or they would be wiped by that reset.
  bind(doc.querySelector("#aircraftFlightsDropzone"), (files) => {
    if (appReadOnlyMode()) return;
    openUploadDialog(selectedAircraftWorkspace());
    if (!assignFilesToInput(fileInput(), files)) {
      showAppToast("This browser can't accept dropped files — use Choose Files.", "error");
    }
  });

  const browse = doc.querySelector("#aircraftFlightsDropzoneBrowse");
  if (browse && browse.dataset?.dropzoneBound !== "1") {
    if (browse.dataset) browse.dataset.dropzoneBound = "1";
    browse.addEventListener("click", () => {
      if (appReadOnlyMode()) return;
      openUploadDialog(selectedAircraftWorkspace());
    });
  }
}
